import React, { useCallback, useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  FormProvider,
  useForm
} from 'react-hook-form'
import {
  useAppSelector
} from '@verzekeringapp-mono/shared/src/hooks/state'
import { useNavigate } from 'react-router-dom'
import { getUser } from '@verzekeringapp-mono/shared/src/state/selectors/user'
import { getResourceAuthorization, getResourceAuthorizations } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'
import { IResourceAuthorization } from '@verzekeringapp-mono/shared/src/models/resource_authorization'
import useAuthentication from '@verzekeringapp-mono/shared/src/hooks/use_authentication'
import { format } from 'date-fns'
import { getTerms } from '@verzekeringapp-mono/shared/src/state/selectors/terms'
import useTerms from '@verzekeringapp-mono/shared/src/hooks/use_terms'
import { FirstnameStep } from './FirstnameStep'
import { TermsStep } from './TermsStep'
import { ResourceAuthorizationsStep } from './ResourceAuthorizationsStep'
import { SuccessStep } from './SuccessStep'

const Setup = () => {
  const [currentStep, setCurrentStep] = React.useState(0)
  const [isLoading, setIsLoading] = React.useState(false)

  const currentResourceAuthorization = useAppSelector(getResourceAuthorization)
  const resourceAuthorizations = useAppSelector(getResourceAuthorizations)
  const [resourceAuthorization, setResourceAuthorization] = React.useState<IResourceAuthorization | null | undefined>(
    currentResourceAuthorization
  )
  const user = useAppSelector(getUser)
  const terms = useAppSelector(getTerms)

  const termsAcceptedAt = useMemo(() => new Date(terms?.acceptedAt ?? 0), [terms?.acceptedAt])
  const termsActiveSince = useMemo(() => terms?.activeSince ? new Date(terms.activeSince) : new Date(0), [terms?.activeSince])
  const termsAcceptedPreviously = useMemo(() => !terms || termsAcceptedAt.getTime() >= termsActiveSince.getTime(), [terms, termsAcceptedAt, termsActiveSince])

  const newResourcesFound = useMemo(() => !resourceAuthorization?.seenAt || resourceAuthorizations.some(ra => !ra.seenAt), [resourceAuthorization, resourceAuthorizations])

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      firstname: user?.firstName ?? '',
      termsRead: !!user?.term.acceptedAt
    }
  })

  const { postOnboarding, logout } = useAuthentication()
  const { acceptTerms } = useTerms()
  const navigate = useNavigate()

  const mappedResourceAuthorizations = useMemo(() => {
    if (!currentResourceAuthorization && resourceAuthorizations) return resourceAuthorizations
    if (!currentResourceAuthorization) return []

    return [currentResourceAuthorization, ...resourceAuthorizations].filter(
      (resourceAuthorization) => resourceAuthorization !== null && resourceAuthorization !== undefined
    )
  }, [resourceAuthorizations, currentResourceAuthorization])

  const checkNameStep = useCallback(() => {
    if (!termsAcceptedPreviously) {
      setCurrentStep(1)
    } else {
      setCurrentStep(2)
    }
  }, [user])

  const checkTermsStep = useCallback(() => {
    if (!terms) return

    if (currentResourceAuthorization && currentResourceAuthorization.active && !newResourcesFound && form.getValues('termsRead')) {
      acceptTerms(terms?.id).then(() => {
        navigate('/home')
      })
    } else if (form.getValues('termsRead')) {
      setCurrentStep(2)
    }
  }, [user, terms])

  useEffect(() => {
    if (!user?.firstName || user?.firstName.length === 0) {
      setCurrentStep(0)
    } else if (!termsAcceptedPreviously) {
      setCurrentStep(1)
    } else {
      setCurrentStep(2)
    }
  }, [user, terms])

  return (
    <div className='flex h-full min-h-screen bg-background-app py-8'>
      <div className='mx-auto flex w-full flex-1 flex-col items-center rounded-lg bg-surface-page p-8 md:max-w-5-xl lg:max-w-[1500px]'>
        <button
          type='button'
          className='flex w-full flex-row justify-end self-start gap-4 text-text-secondary transition-colors hover:text-text-brand'
          onClick={logout}
        >
          Uitloggen
          <FontAwesomeIcon
            icon='user-times'
            className='w-[24px] h-[24px]'
          />
        </button>

        <FormProvider {...form}>
          <form
            className='w-full items-center flex flex-1'
            onSubmit={form.handleSubmit(data => {
              if (resourceAuthorization) {
                const relationNumbersSeen = mappedResourceAuthorizations.map(
                  (resourceAuthorization) => resourceAuthorization.relationNumber
                )

                setIsLoading(true)

                postOnboarding({
                  firstName: data.firstname,
                  termId : terms?.id,
                  termAcceptedAt: data.termsRead ? `${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}.000` : null,
                  relationNumbersSeen: relationNumbersSeen, //Array of all relation numbers of available resource_authorizations (relations) so we knows the user has seen them.
                  relationNumber: resourceAuthorization.relationNumber //Should be the relation number that is picked as the default resource_authorization (relation) after the login of a user,
                }).then(() => {
                  setCurrentStep(3)
                }).finally(() => {
                  setIsLoading(false)
                })
              }
            })}
          >
            {currentStep === 0 && (
              <FirstnameStep onNextStep={() => checkNameStep()} />
            )}

            {currentStep === 1 && (
              <TermsStep
                onPreviousStep={() => setCurrentStep(0)}
                onNextStep={() => checkTermsStep()}
                termsAcceptedPreviously={termsAcceptedPreviously}
              />
            )}

            {currentStep === 2 && (
              <ResourceAuthorizationsStep
                setResourceAuthorization={setResourceAuthorization}
                currentResourceAuthorization={resourceAuthorization}
                isLoading={isLoading}
              />
            )}

            {currentStep === 3 && <SuccessStep />}
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default Setup
